/**
 * SliderControl - Number slider with min/max from schema
 *
 * Shows current value, min, max labels, and live preview.
 */

interface SliderControlProps {
  label: string;
  value: number;
  min: number;
  max: number;
  description?: string;
  error?: string;
  onChange: (value: number) => void;
  disabled?: boolean;
}

export function SliderControl({
  label,
  value,
  min,
  max,
  description,
  error,
  onChange,
  disabled = false,
}: SliderControlProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(Number(e.target.value));
  };

  return (
    <div
      className="mb-4"
      data-testid={`slider-control-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {/* Label and current value */}
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium">{label}</label>
        <span
          className="text-sm font-bold text-purple-400"
          data-testid="slider-value"
        >
          {value}
        </span>
      </div>

      {/* Slider */}
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        className={`w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        style={{
          background: `linear-gradient(to right, #a855f7 0%, #a855f7 ${((value - min) / (max - min)) * 100}%, rgba(255,255,255,0.2) ${((value - min) / (max - min)) * 100}%, rgba(255,255,255,0.2) 100%)`,
        }}
      />

      {/* Min/Max labels */}
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>Min: {min}</span>
        <span>Max: {max}</span>
      </div>

      {/* Description */}
      {description && (
        <p className="text-xs text-gray-400 mt-1">{description}</p>
      )}

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-400 mt-1" data-testid="slider-error">
          {error}
        </p>
      )}
    </div>
  );
}
